import React, { FC, useEffect } from 'react'
import styles from 'styles/components/template.module.scss'
import startOfMonth from 'date-fns/startOfMonth'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getBread, getBreadLabels } from 'reducks/bread/selectors'
import { getSchedules } from 'reducks/schedule/selectors'
import { Spacer } from 'components/Utility'
import { MainHeadline } from 'components/Typography'
import { NavButton, OrangeButton } from 'components/Button'
import { goBack } from 'connected-react-router'
import { CommonSettings, DeliveryBreadSettings, ScheduleSettings } from 'components/Product'
import { saveBreadWithSchedules } from 'reducks/bread/operations'
import { useSchedulesOfType } from 'hooks/schedule'
import { useCategoryNameOfBread, useFetchCategories } from 'hooks/category'
import { modifyStockAmountToZero, fetchFutureStocks } from 'reducks/stock/operations'
import { getStoreRef } from 'reducks/auth/selectors'
import { getStocks } from 'reducks/stock/selectors'
import { AlertCard } from 'components/Card'
import { fetchStoreCalendars } from 'reducks/storeCalendar/operations'
import { getStoreCalendars } from 'reducks/storeCalendar/selectors'
import { getStoreDeliveryBread } from 'reducks/storeDeliveryBread/selectors'
import { useFetchLabels } from 'hooks/label'
import { useExtractOptions } from 'hooks/bread'

type Props = {
  isEditPage: boolean
}

const ProductEditConfirmTemplate: FC<Props> = (props) => {
  const { isEditPage } = props
  const dispatch = useDispatch()
  const bread = useSelector((state: State) => getBread(state))
  const breadLabels = useSelector((state: State) => getBreadLabels(state))
  const categories = useFetchCategories()
  const schedules = useSelector((state: State) => getSchedules(state))
  const storeCalendars = useSelector((state: State) => getStoreCalendars(state))
  const stocks = useSelector((state: State) => getStocks(state))
  const storeDeliveryBread = useSelector((state: State) => getStoreDeliveryBread(state))
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const dailySchedules = useSchedulesOfType(schedules, 'daily')
  const preorderSchedules = useSchedulesOfType(schedules, 'preorder')
  const categoryName = useCategoryNameOfBread(bread, categories)
  const labels = useFetchLabels()
  const options = useExtractOptions(schedules, storeDeliveryBread)

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchFutureStocks({ storeRef, types: ['daily', 'preorder', 'delivery'], breadRef: bread.bread_ref }))
  }, [storeRef])

  useEffect(() => {
    if (!storeRef) return
    dispatch(fetchStoreCalendars(storeRef, startOfMonth(new Date())))
  }, [storeRef])

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'登録内容の確認'} />
      <Spacer size={20} />
      <CommonSettings
        name={bread.name}
        shortName={bread.short_name || ''}
        imageUrls={bread.image_urls}
        description={bread.description}
        categoryName={categoryName}
        price={bread.price}
        allergens={bread.allergens}
        isSetAllergens={bread.is_set_allergens}
        isTypeDaily={bread.is_type_daily}
        isTypePreorder={bread.is_type_preorder}
        isTypeDelivery={bread.is_type_delivery}
        status={bread.status}
        options={options}
        breadLabels={breadLabels}
        labels={labels}
      />
      {dailySchedules.length > 0 && (
        <>
          <Spacer size={28} />
          <ScheduleSettings schedules={dailySchedules} type={'daily'} />
        </>
      )}
      {preorderSchedules.length > 0 && (
        <>
          <Spacer size={28} />
          <ScheduleSettings schedules={preorderSchedules} type={'preorder'} />
        </>
      )}
      {storeDeliveryBread.bread_ref && (
        <>
          <Spacer size={28} />
          <DeliveryBreadSettings storeDeliveryBread={storeDeliveryBread} />
        </>
      )}
      <Spacer size={28} />
      <AlertCard
        headline={
          '商品情報は、即時反映され個別に設定している情報も上書きされます。\nすでに注文を受け付けた商品や当日の在庫数に関しては、上書きされません。'
        }
        title={'注意'}
      />
      <Spacer size={48} />
      <OrangeButton
        label={'保存する'}
        onClick={() => {
          dispatch(
            saveBreadWithSchedules(
              bread,
              breadLabels,
              schedules,
              storeDeliveryBread,
              stocks,
              storeCalendars.filter((storeCalendar) => storeCalendar.status === 'open'),
              isEditPage
            )
          )
          if (bread.status === 'private' && stocks.length > 0) {
            dispatch(modifyStockAmountToZero(stocks))
          }
        }}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={12} />
      <NavButton
        label={'前のステップへ戻る'}
        iconPath={'/icon/grey/arrow_prev.svg'}
        onClick={() => dispatch(goBack())}
        url={''}
      />
      <Spacer size={72} />
    </div>
  )
}

export default ProductEditConfirmTemplate
